import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, output, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import type { CommonPortfolioSearchResult } from '../commonportfolio.service';
import { CommonPortfolioService } from '../commonportfolio.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { AppNotificationService } from '../app-notification.service';

@Component({
  selector: 'app-portfolio-selector',
  imports: [CommonModule, ReactiveFormsModule, MatAutocompleteModule, MatIconModule],
  templateUrl: './portfolio-selector.html',
  styleUrl: './portfolio-selector.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PortfolioSelectorComponent {
  private readonly portfolioService = inject(CommonPortfolioService);
  private readonly notification = inject(AppNotificationService);

  // Reactive form control for the autocomplete input
  readonly queryCtrl = new FormControl('');
  // Signal mirror of the input text so computed values can react to typing
  protected readonly query = signal('');

  // Local state signals
  protected readonly loading = signal(false);
  protected readonly selected = signal<CommonPortfolioSearchResult | null>(null);
  readonly selectionChange = output<CommonPortfolioSearchResult>();
  // External trigger to clear selection & text (incremental counter pattern)
  readonly clearTrigger = input<number>(0);
  // Optional initial portfolio to pre-populate the selector
  readonly initialPortfolio = input<CommonPortfolioSearchResult | null>(null);
  // Optional externally-provided list of portfolios to show in the dropdown
  readonly portfolios = input<CommonPortfolioSearchResult[] | null>(null);
  // Recently opened groups, pinned above the full list so the common case is one click
  readonly recents = input<CommonPortfolioSearchResult[] | null>(null);

  // Stream of autocomplete options derived from queryCtrl value changes
  private readonly options$ = this.queryCtrl.valueChanges.pipe(
    filter(v => (v?.length ?? 0) >= 3),
    debounceTime(200),
    distinctUntilChanged(),
    tap(() => {
      this.loading.set(true);
    }),
    switchMap((term: string | null) => this.portfolioService.search(term ?? '').pipe(
      tap(() => this.loading.set(false)),
      catchError((err) => {
        this.loading.set(false);
        this.notification.showErrorObjectMessage(err);
        // Return empty list so stream continues (no hard error propagation)
        return of([] as CommonPortfolioSearchResult[]);
      })
    ))
  );

  // Convert options observable to signal for template usage
  protected readonly options = toSignal(this.options$, { initialValue: [] as CommonPortfolioSearchResult[] });

  /**
   * The groups this user can reach, fetched by the selector itself when the host does not supply
   * them. Most hosts never passed a list, so without this their dropdown is empty until three
   * characters are typed — which the control no longer advertises.
   */
  private readonly fallbackGroups = signal<CommonPortfolioSearchResult[]>([]);
  private fallbackRequested = false;

  private readonly _loadFallback = effect(() => {
    const provided = this.portfolios();
    if (this.fallbackRequested || (provided && provided.length > 0)) return;
    this.fallbackRequested = true;

    this.portfolioService.getAccessibleGroups().subscribe({
      next: groups => this.fallbackGroups.set(groups),
      error: () => this.fallbackGroups.set([])
    });
  });

  // Use externally-provided portfolios when present and user hasn't typed a search term.
  // If the user types a query of length >= 3, prefer live API search results.
  protected readonly effectiveOptions = computed(() => {
    const q = (this.query() ?? '').toString();
    const provided = this.portfolios();
    if ((q.length ?? 0) >= 3) {
      return this.options();
    }
    if (provided && provided.length > 0) return provided;

    const fallback = this.fallbackGroups();
    return fallback.length > 0 ? fallback : this.options();
  });

  /** True once the user has typed enough to be searching rather than browsing their own list. */
  protected readonly isSearching = computed(() => (this.query() ?? '').length >= 3);

  /**
   * Recents, capped so the pinned section stays scannable.
   *
   * Falls back to the list the dashboard persists, so a host that does not track recents — the
   * navbar, for one — still gets them without having to own that state.
   */
  protected readonly recentOptions = computed(() => {
    const provided = this.recents();
    const source = provided && provided.length > 0 ? provided : this.storedRecents();
    return PortfolioSelectorComponent.dedupeByName(
      source.filter(r => r.portfolioGroupId != null)).slice(0, 5);
  });

  private static readonly RECENT_PORTFOLIOS_KEY = 'recentPortfolios';

  private readonly storedRecents = signal<CommonPortfolioSearchResult[]>(
    PortfolioSelectorComponent.loadStoredRecents());

  private static loadStoredRecents(): CommonPortfolioSearchResult[] {
    try {
      const raw = localStorage.getItem(PortfolioSelectorComponent.RECENT_PORTFOLIOS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      // Unreadable or blocked storage just means no recents to pin.
      return [];
    }
  }

  /**
   * The full accessible list with recents removed, so a group never appears twice in one dropdown.
   * Exclusion is by NAME, not id — a recent pinned under one registry id must also suppress its
   * duplicate-name twin from the full list.
   */
  protected readonly remainingOptions = computed(() => {
    const recentNames = new Set(this.recentOptions()
      .map(r => (r.portfolioGroupName ?? r.name ?? '').trim().toLowerCase()));
    return PortfolioSelectorComponent.dedupeByName(this.effectiveOptions())
      .filter(o => !recentNames.has((o.portfolioGroupName ?? o.name ?? '').trim().toLowerCase()));
  });

  /**
   * Label for the closed control: the chosen group's NAME only, or a prompt when nothing is
   * chosen. The group id is deliberately not shown anywhere in this control: several registry ids
   * can share one group name and every query aggregates them by name, so an id would imply a
   * distinction the app does not make.
   */
  protected readonly displayLabel = computed(() => {
    const s = this.selected();
    return s ? PortfolioSelectorComponent.label(s) : '';
  });

  /**
   * One dropdown row per group NAME (case-insensitive), keeping the first occurrence. The registry
   * carries duplicate names under different ids; since selection is aggregated by name, showing
   * both renders as an inexplicable identical twin row.
   */
  static dedupeByName(list: CommonPortfolioSearchResult[]): CommonPortfolioSearchResult[] {
    const seen = new Set<string>();
    return list.filter(o => {
      const key = (o.portfolioGroupName ?? o.name ?? '').trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /** The live-search results as shown: deduped by name. */
  protected readonly searchOptions = computed(() =>
    PortfolioSelectorComponent.dedupeByName(this.options()));

  protected clearSelection(event: Event): void {
    event.stopPropagation();
    this.selected.set(null);
    this.queryCtrl.setValue('', { emitEvent: true });
    this.cleared.emit();
  }

  /** Emitted when the user clears the selection from inside the control. */
  readonly cleared = output<void>();

  // Computed helper to show a message when nothing found.
  // Uses this.query() (signal, updated only via valueChanges) instead of queryCtrl.value so that
  // programmatic pre-population via setValue(..., {emitEvent:false}) never triggers "No results".
  protected readonly nothingFound = computed(() =>
    PortfolioSelectorComponent.showNoResults(
      this.loading(), this.options().length, this.query().length, this.selected() !== null));

  /**
   * "No results" is only true while the user is actually searching and the search came back
   * empty. A selection fills the input with the group's name — which is ≥3 characters while the
   * live-search list is still empty — so without the selection guard the hint sat under every
   * successfully selected group, reading as a broken search. Static so the rule is testable
   * without building the component.
   */
  static showNoResults(loading: boolean, optionCount: number, queryLength: number, hasSelection: boolean): boolean {
    return !loading && optionCount === 0 && queryLength >= 3 && !hasSelection;
  }

  /**
   * Typing over a selection drops it, so the control stops claiming a group the input no longer
   * names — the X and the selected styling go with it.
   *
   * This read `queryCtrl.value` directly, which is not a signal, so the effect had no reactive
   * dependency at all: it ran once at construction and never again. It also compared against
   * `selected()?.name`, while the input is filled with `portfolioGroupName` — so even reactive it
   * would have cleared a valid selection immediately. Compare against the same label the control
   * writes, and read the `query` signal that mirrors the control.
   */
  private readonly _resetOnType = effect(() => {
    const typed = this.query();
    const current = this.selected();
    if (current && typed !== PortfolioSelectorComponent.label(current)) {
      this.selected.set(null);
    }
  });

  /** The text put in the input for a selection — programmatic writes must use this too. */
  private static label(portfolio: CommonPortfolioSearchResult): string {
    return portfolio.portfolioGroupName ?? portfolio.name;
  }

  /**
   * Writes the selection's label into the control WITHOUT firing a search for it, and keeps the
   * `query` signal in step by hand. `emitEvent: false` skips the valueChanges subscription that
   * normally maintains that signal, and a stale `query` would read as "the user has typed
   * something else" to the effect above and clear the selection that was just made.
   */
  private showSelectedLabel(portfolio: CommonPortfolioSearchResult): void {
    const text = PortfolioSelectorComponent.label(portfolio);
    this.queryCtrl.setValue(text, { emitEvent: false });
    this.query.set(text);
  }

  // Effect: when clearTrigger increments, clear selection and text
  private readonly _externalClear = effect(() => {
    this.clearTrigger(); // subscribe
    this.selected.set(null);
    this.queryCtrl.setValue('', { emitEvent: true });
  });

  /**
   * Pre-populate from the initialPortfolio input, once, when it first arrives.
   *
   * It does NOT emit selectionChange. Showing the group the host already knows about is a display
   * concern, but every host treats that output as "the user picked a group": the navbar routes to
   * it. So on a deep link to /dashboard/1405/match-review the dashboard resolved the group, passed
   * it here, this fired, and the navbar navigated to /dashboard/1405 — whose empty child route
   * redirects to the properties list. Any pasted child URL landed somewhere else, which is 367037.
   * A host that needs to react to this value already has it: it is the one that supplied it.
   */
  //
  // Applied whenever the host's value names a DIFFERENT group from the one shown, not only the
  // first time (375350). The one-shot version left the banner reading the previously selected
  // client after a deep link or Back landed on another client's route: the page showed ADM Global
  // Leases and the selector still said Thomson Reuters. Hosts pass the group resolved from the
  // route, so following a change here is following the URL. Compared by name, which is the key
  // every list in this control is deduplicated on. Same group, or the host has nothing yet: no-op,
  // so a user's own pick is never overwritten while they are still on that client.
  private readonly _applyInitial = effect(() => {
    const initial = this.initialPortfolio();
    if (!initial) return;
    const current = this.selected();
    if (current && PortfolioSelectorComponent.sameGroup(current, initial)) return;
    this.selected.set(initial);
    this.showSelectedLabel(initial);
  });

  /** Public for the spec, like the other pure rules in this file. */
  static sameGroup(a: CommonPortfolioSearchResult, b: CommonPortfolioSearchResult): boolean {
    return (a.portfolioGroupName ?? '').trim().toLowerCase() === (b.portfolioGroupName ?? '').trim().toLowerCase();
  }

  constructor() {
    // Keep the `query` signal in sync with the FormControl so computed reacts to typing
    this.queryCtrl.valueChanges.subscribe(v => this.query.set(v ?? ''));
  }

  optionSelected(option: CommonPortfolioSearchResult) {
    this.selected.set(option);
    this.showSelectedLabel(option);
    this.selectionChange.emit(option);
  }
}
